import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useApp } from "../lib/store";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Users, Mail, Phone, Smartphone, Linkedin, Cake, Languages, Crown } from "lucide-react";

const ROLE_META = {
  "Decision Maker": { key: "role_decision_maker", chip: "bg-[#1B2A4A] text-white", dot: "bg-[#1B2A4A]", ring: "ring-[#1B2A4A]/30" },
  Champion: { key: "role_champion", chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", ring: "ring-emerald-400/40" },
  Influencer: { key: "role_influencer", chip: "bg-blue-100 text-blue-700", dot: "bg-blue-500", ring: "ring-blue-400/40" },
  Gatekeeper: { key: "role_gatekeeper", chip: "bg-amber-100 text-amber-700", dot: "bg-amber-500", ring: "ring-amber-400/40" },
  User: { key: "role_user", chip: "bg-slate-100 text-slate-600", dot: "bg-slate-400", ring: "ring-slate-300/50" },
};

const TIERS = [
  { id: "t1", label: "orgchart_tier_decision", roles: ["Decision Maker"] },
  { id: "t2", label: "orgchart_tier_influence", roles: ["Champion", "Influencer"] },
  { id: "t3", label: "orgchart_tier_operational", roles: ["Gatekeeper", "User"] },
];

function initials(c) {
  return `${(c.first_name || "?")[0] || ""}${(c.last_name || "")[0] || ""}`.toUpperCase();
}

function ContactNode({ c, t, index }) {
  const meta = ROLE_META[c.buying_role];
  const roleLabel = meta ? t(meta.key) : c.buying_role || t("orgchart_no_role");
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={`group w-52 text-left bg-white border border-gray-200 rounded-xl px-3.5 py-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 ring-1 ${meta ? meta.ring : "ring-transparent"}`}
            data-testid={`orgchart-node-${c.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <div className="h-10 w-10 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-xs font-bold font-mono">
                  {initials(c)}
                </div>
                <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${meta ? meta.dot : "bg-gray-300"}`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{c.first_name} {c.last_name}</p>
                <p className="text-xs text-gray-500 truncate">{c.title || "–"}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
              <Badge className={`${meta ? meta.chip : "bg-gray-100 text-gray-600"} hover:bg-inherit text-[10px] px-2 py-0`}>{roleLabel}</Badge>
              {c.department && <span className="text-[10px] text-gray-400">{c.department}</span>}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-72" data-testid={`orgchart-popover-${c.id}`}>
          <div className="space-y-2.5">
            <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
              <div className="h-11 w-11 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-sm font-bold font-mono shrink-0">{initials(c)}</div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{c.first_name} {c.last_name}</p>
                <p className="text-xs text-gray-500 truncate">{c.title || "–"}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge className={`${meta ? meta.chip : "bg-gray-100 text-gray-600"} hover:bg-inherit text-[11px]`}>{roleLabel}</Badge>
              {c.department && <Badge variant="outline" className="text-[11px]">{c.department}</Badge>}
            </div>
            <div className="space-y-1.5 text-xs text-gray-600 pt-1">
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-[#1B2A4A] break-all"><Mail className="h-3.5 w-3.5 text-gray-400 shrink-0" />{c.email}</a>}
              {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gray-400 shrink-0" />{c.phone}</p>}
              {c.mobile && <p className="flex items-center gap-2"><Smartphone className="h-3.5 w-3.5 text-gray-400 shrink-0" />{c.mobile}</p>}
              {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#1B2A4A] break-all"><Linkedin className="h-3.5 w-3.5 text-gray-400 shrink-0" />LinkedIn</a>}
              {c.preferred_language && <p className="flex items-center gap-2"><Languages className="h-3.5 w-3.5 text-gray-400 shrink-0" />{c.preferred_language}</p>}
              {c.birthday && <p className="flex items-center gap-2"><Cake className="h-3.5 w-3.5 text-gray-400 shrink-0" />{c.birthday}</p>}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </motion.div>
  );
}

export function OrgChart({ contacts = [] }) {
  const { t } = useApp();
  const [roleFilter, setRoleFilter] = useState(null);

  const { tiers, others, counts } = useMemo(() => {
    const known = new Set(TIERS.flatMap((tr) => tr.roles));
    const filtered = roleFilter ? contacts.filter((c) => c.buying_role === roleFilter) : contacts;
    const tiers = TIERS.map((tr) => ({ ...tr, people: filtered.filter((c) => tr.roles.includes(c.buying_role)) }));
    const others = filtered.filter((c) => !known.has(c.buying_role));
    const counts = {};
    contacts.forEach((c) => { counts[c.buying_role] = (counts[c.buying_role] || 0) + 1; });
    return { tiers, others, counts };
  }, [contacts, roleFilter]);

  if (!contacts.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-sm text-gray-400" data-testid="orgchart-empty">
        {t("orgchart_empty")}
      </div>
    );
  }

  let globalIndex = 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 sm:p-7" data-testid="orgchart-panel">
      {/* Header + legend */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#1B2A4A] flex items-center justify-center"><Users className="h-4.5 w-4.5 text-[#F5A623]" /></div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t("orgchart_title")}</h3>
            <p className="text-xs text-gray-400">{t("orgchart_subtitle")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="orgchart-legend">
          {Object.entries(ROLE_META).map(([role, meta]) => (
            <button
              key={role}
              onClick={() => setRoleFilter((p) => (p === role ? null : role))}
              className={`flex items-center gap-1.5 text-[11px] rounded-full px-2.5 py-1 border transition-colors duration-150 ${roleFilter === role ? "border-[#1B2A4A] bg-[#1B2A4A]/5" : "border-gray-200 hover:bg-gray-50"}`}
              data-testid={`orgchart-legend-${meta.key}`}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
              {t(meta.key)}
              <span className="text-gray-400 font-mono">{counts[role] || 0}</span>
            </button>
          ))}
          {roleFilter && (
            <button onClick={() => setRoleFilter(null)} className="text-[11px] text-[#1B2A4A] underline ml-1" data-testid="orgchart-clear-filter">{t("orgchart_show_all")}</button>
          )}
        </div>
      </div>

      {/* Tiers */}
      <div className="relative space-y-8">
        {tiers.map((tr) => (
          <div key={tr.id} className="relative" data-testid={`orgchart-tier-${tr.id}`}>
            <div className="flex items-center gap-2 mb-3">
              {tr.id === "t1" && <Crown className="h-3.5 w-3.5 text-[#F5A623]" />}
              <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-400">{t(tr.label)}</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
            {tr.people.length ? (
              <div className="flex flex-wrap gap-3">
                {tr.people.map((c) => <ContactNode key={c.id} c={c} t={t} index={globalIndex++} />)}
              </div>
            ) : (
              <p className="text-xs text-gray-300 italic pl-1">{t("orgchart_tier_empty")}</p>
            )}
          </div>
        ))}

        {others.length > 0 && (
          <div className="relative" data-testid="orgchart-tier-other">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-400">{t("orgchart_no_role")}</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
            <div className="flex flex-wrap gap-3">
              {others.map((c) => <ContactNode key={c.id} c={c} t={t} index={globalIndex++} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
